import React, { useState, useEffect } from "react";
import type { TraceItem } from "../types";
import { attachRating, attachComment } from "../api";
import { SubTraceModal } from "./SubTraceModal";
import { SourceDetailModal } from "./SourceDetailModal";
import { TraceOverview } from "./TraceOverview";
import { TraceHeader } from "./TraceHeader";
import { TraceInteractions } from "./TraceInteractions";
import { TraceAnnotation } from "./TraceAnnotation";
import { TraceComments } from "./TraceComments";
import { AutomatedEvaluations } from "./AutomatedEvaluations";
import { JsonTraceModal } from "./JsonTraceModal";

interface TraceDetailPaneProps {
  trace: TraceItem | null;
  loading: boolean;
  onRatingChange: (id: string, rating: boolean) => void;
  onCommentAdded: (id: string, comment: { id: string; trace_id: string; comment: string; created_at: string }) => void;
}

export function TraceDetailPane({ trace, loading, onRatingChange, onCommentAdded }: TraceDetailPaneProps) {
  const [showComment, setShowComment] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [selectedSubTrace, setSelectedSubTrace] = useState<{
    data: Record<string, any>;
    evaluatorName: string | null;
  } | null>(null);
  const [selectedSource, setSelectedSource] = useState<{ type: string; id: string } | null>(null);
  const [showJsonTrace, setShowJsonTrace] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedSourceId, setCopiedSourceId] = useState<string | null>(null);
  const [copiedExplanationId, setCopiedExplanationId] = useState<string | null>(null);

  useEffect(() => {
    setShowComment(trace?.rating === false);
    setCommentText("");
    setSubmitting(false);
    setSelectedSubTrace(null);
    setSelectedSource(null);
    setShowJsonTrace(false);
    setCopied(false);
    setCopiedSourceId(null);
    setCopiedExplanationId(null);
  }, [trace?.id]);

  const handleCopyId = () => {
    if (!trace) return;
    navigator.clipboard.writeText(trace.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyExplanation = (id: string) => {
    setCopiedExplanationId(id);
    setTimeout(() => setCopiedExplanationId(null), 2000);
  };

  const handleCopySource = (id: string) => {
    setCopiedSourceId(id);
    setTimeout(() => setCopiedSourceId(null), 2000);
  };

  const handleGoodTrace = async () => {
    if (!trace) return;
    onRatingChange(trace.id, true);
    setShowComment(false);
    try {
      await attachRating(trace.id, true);
    } catch {
      onRatingChange(trace.id, false);
    }
  };

  const handleBadTrace = () => {
    if (!trace) return;
    onRatingChange(trace.id, false);
    setShowComment(true);
  };

  const handleSubmitComment = async () => {
    if (!trace || !commentText.trim()) return;
    setSubmitting(true);
    const text = commentText.trim();
    try {
      await Promise.all([
        attachRating(trace.id, false),
        attachComment(trace.id, text),
      ]);
      onCommentAdded(trace.id, {
        id: crypto.randomUUID(),
        trace_id: trace.id,
        comment: text,
        created_at: new Date().toISOString(),
      });
      setCommentText("");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground animate-pulse">Loading…</p>;
  }

  if (!trace) {
    return <p className="text-sm text-muted-foreground">Select a trace to view details.</p>;
  }

  return (
    <div className="space-y-6">
      <TraceHeader
        traceId={trace.id}
        createdAt={trace.created_at}
        copied={copied}
        onCopy={handleCopyId}
      />

      <TraceOverview trace={trace} />

      <TraceInteractions trace={trace.trace} onViewJsonTrace={() => setShowJsonTrace(true)} />

      <TraceAnnotation
        rating={trace.rating}
        showComment={showComment}
        commentText={commentText}
        submitting={submitting}
        onCommentTextChange={setCommentText}
        onGoodTrace={handleGoodTrace}
        onBadTrace={handleBadTrace}
        onSubmitComment={handleSubmitComment}
        onCancelComment={() => {
          setShowComment(false);
          setCommentText("");
        }}
      />

      <TraceComments comments={trace.comments} />

      <AutomatedEvaluations
        evaluations={trace.evaluations}
        copiedExplanationId={copiedExplanationId}
        copiedSourceId={copiedSourceId}
        onCopyExplanation={handleCopyExplanation}
        onCopySource={handleCopySource}
        onViewSubTrace={(data, evaluatorName) =>
          setSelectedSubTrace({ data, evaluatorName })
        }
        onSourceClick={(type, id) => setSelectedSource({ type, id })}
      />

      {selectedSubTrace && (
        <SubTraceModal
          subTrace={selectedSubTrace.data}
          evaluatorName={selectedSubTrace.evaluatorName}
          onClose={() => setSelectedSubTrace(null)}
        />
      )}

      {selectedSource && (
        <SourceDetailModal
          source={selectedSource}
          onClose={() => setSelectedSource(null)}
        />
      )}

      {showJsonTrace && (
        <JsonTraceModal
          data={trace.trace}
          onClose={() => setShowJsonTrace(false)}
        />
      )}
    </div>
  );
}
